import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { QuotaLane, SkuRateLimitRecord } from '@nadoutong/contracts';
import { useSkuRateLimits } from '../../api/useRoutingLoad.ts';
import { useT } from '../../i18n/index.ts';
import { Panel } from '../../ui/index.ts';
import { formatTpmCap } from '../../pages/quotas/tpmUnits.ts';

function rowForSku(
  items: readonly SkuRateLimitRecord[] | undefined,
  skuId: string,
): SkuRateLimitRecord | null {
  if (items === undefined) return null;
  return items.find((item) => item.sku_id === skuId) ?? null;
}

export function SkuRateLimitPanel({ skuId }: { readonly skuId: string }): JSX.Element {
  const t = useT();
  const limits = useSkuRateLimits();
  const row = useMemo(() => rowForSku(limits.data?.items, skuId), [limits.data, skuId]);
  const quotaLane = useMemo((): QuotaLane => {
    const listed = limits.data?.quota_lanes?.find((item) => item.sku_id === skuId);
    if (listed !== undefined) return listed.quota_lane;
    const task = limits.data?.task_lane_defaults?.some((item) => item.sku_id === skuId) === true;
    return task ? 'task' : 'llm';
  }, [limits.data, skuId]);
  const unlimited = t('sku_detail.rate_limit.unlimited');
  const image = quotaLane === 'image';

  return (
    <Panel caption={t('sku_detail.rate_limit.title')} padded testId="sku-rate-limit-panel">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense leading-5 text-paper-low">{t('sku_detail.rate_limit.hint')}</p>
        <Link
          to={`/quotas?sku=${encodeURIComponent(skuId)}`}
          className="text-dense text-signal hover:text-signal-bright"
          data-testid="sku-rate-limit-edit-link"
        >
          {t('quotas.link_edit')}
        </Link>
        {limits.isPending ? (
          <p className="m-0 text-dense text-paper-low">{t('sku_detail.rate_limit.loading')}</p>
        ) : (
          <>
            <p className="m-0 font-mono text-dense text-paper" data-testid="sku-rate-limit-summary">
              {t('sku_detail.rate_limit.rpm')}: {row?.rpm === null || row?.rpm === undefined ? unlimited : String(row.rpm)}
              {' / '}
              {t('sku_detail.rate_limit.tpm')}: {image ? '—' : formatTpmCap(row?.tpm, unlimited)}
              {' / '}
              {t('sku_detail.rate_limit.tpm_uncached')}: {image ? '—' : formatTpmCap(row?.tpm_uncached, unlimited)}
            </p>
            {image ? (
              <p className="m-0 text-dense text-paper-low" data-testid="sku-rate-limit-image-lane">
                {t('quotas.image_lane.rpm_only')}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}
